import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, Avatar, Chip, Divider, Tooltip, Container, Paper, Button, IconButton, Fab } from '@mui/material';
import { UploadFile, Summarize, Tune, AutoAwesome, CheckCircle, RadioButtonUnchecked, FiberManualRecord, Description, Logout, Chat } from '@mui/icons-material';
import { authService } from './services/api';
import StepUpload from './components/StepUpload';
import StepSummary from './components/StepSummary';
import StepConfig from './components/StepConfig';
import StepProposal from './components/StepProposal';
import ChatPanel from './components/ChatPanel';

const DRAWER_WIDTH = 280;

const theme = createTheme({
  palette: {
    primary: { main: '#6D28D9' },
    secondary: { main: '#DB2777' },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text: { primary: '#1E293B', secondary: '#64748B' },
  },
  typography: { fontFamily: '"Inter","Roboto",sans-serif' },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, borderRadius: 10 } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
  },
});

const STEPS = [
  { id: 1, label: 'Upload RFP', icon: <UploadFile />, desc: 'Upload case study document' },
  { id: 2, label: 'Analyze & Review', icon: <Summarize />, desc: 'AI summary + human review' },
  { id: 3, label: 'Configure', icon: <Tune />, desc: 'Edit sections, rules, diagrams' },
  { id: 4, label: 'Generate', icon: <AutoAwesome />, desc: 'AI proposal + compliance' },
];

// Login Page
function LoginPage() {
  const handleLogin = async () => {
    try {
      const { authUrl } = await authService.getAuthUrl();
      window.location.href = authUrl;
    } catch (err) {
      alert('Login failed: ' + err.message);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1E1E1E 0%, #3B1F5B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Container maxWidth="sm">
        <Paper sx={{ textAlign: 'center', borderRadius: '32px', p: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 3, background: 'linear-gradient(135deg, #6D28D9, #3B1F5B)' }}>
            <Description sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h4" sx={{ background: 'linear-gradient(45deg, #6D28D9, #3B1F5B)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 2 }} fontWeight={700}>
            Proposal Generator
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, fontWeight: 400 }}>
            AI-Powered Technical Proposal Workflow
          </Typography>
          <Button variant="contained" size="large" onClick={handleLogin}
            sx={{ background: 'linear-gradient(45deg, #6D28D9 30%, #DB2777 90%)', px: 4, py: 1.5, fontSize: 16, borderRadius: '16px',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 40px rgba(109,40,217,0.4)' } }}>
            Sign in with Microsoft
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}

// Callback Page
function CallbackPage() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      authService.handleCallback(code)
        .then(() => { window.location.href = '/'; })
        .catch(() => { window.location.href = '/login'; });
    } else {
      window.location.href = '/login';
    }
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1E1E1E, #3B1F5B)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper sx={{ textAlign: 'center', borderRadius: '24px', p: 4, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <Typography variant="h6" color="text.secondary">Processing authentication...</Typography>
      </Paper>
    </Box>
  );
}

// Main App with Sidebar
function MainApp() {
  const [step, setStep] = useState(1);
  const [session, setSession] = useState({});
  const [chatOpen, setChatOpen] = useState(false);
  const email = localStorage.getItem('userEmail') || '';

  if (!authService.isAuthenticated()) return <Navigate to="/login" replace />;

  const reqCount = session.analysis?.requirement_count || 0;
  const sectionCount = (session.config || session.analysis?.config)?.proposal_sections?.filter(s => s.include !== false).length || 0;
  const diagramCount = (session.config || session.analysis?.config)?.diagrams?.length || 0;
  const comp = session.proposalResult?.compliance || {};
  const coverage = comp.coverage_percentage || null;

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Sidebar */}
      <Drawer variant="permanent" sx={{
        width: DRAWER_WIDTH, flexShrink: 0,
        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: '#FFFFFF', borderRight: '1px solid #E2E8F0', boxSizing: 'border-box' },
      }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#6D28D9', width: 40, height: 40 }}><AutoAwesome sx={{ fontSize: 22 }} /></Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Proposal AI</Typography>
            <Typography variant="caption" color="text.secondary">Agentic Workflow</Typography>
          </Box>
        </Box>

        <Divider />

        {/* User */}
        {email && (
          <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>{email}</Typography>
            <IconButton size="small" onClick={handleLogout} sx={{ color: '#94A3B8' }}><Logout fontSize="small" /></IconButton>
          </Box>
        )}

        <Divider />

        {/* Steps */}
        <Box sx={{ px: 1.5, py: 2 }}>
          <Typography variant="overline" sx={{ px: 1.5, color: '#94A3B8', fontSize: 10, letterSpacing: 1.5 }}>Workflow</Typography>
          <List sx={{ mt: 1 }}>
            {STEPS.map((s) => {
              const isActive = step === s.id;
              const isDone = step > s.id;
              const isLocked = step < s.id;
              return (
                <ListItemButton key={s.id} selected={isActive} onClick={() => { if (isDone) setStep(s.id); }}
                  sx={{ borderRadius: 2, mb: 0.5, py: 1.5, bgcolor: isActive ? '#F3E8FF' : 'transparent',
                    border: isActive ? '1px solid #D8B4FE' : '1px solid transparent', opacity: isLocked ? 0.4 : 1,
                    '&:hover': { bgcolor: isDone ? '#FAF5FF' : undefined } }}>
                  <ListItemIcon sx={{ minWidth: 36, color: isDone ? '#16A34A' : isActive ? '#6D28D9' : '#CBD5E1' }}>
                    {isDone ? <CheckCircle fontSize="small" /> : isActive ? <FiberManualRecord fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={isActive ? 700 : 500} color={isActive ? '#1E293B' : '#64748B'}>{s.label}</Typography>}
                    secondary={<Typography variant="caption" color="#94A3B8">{s.desc}</Typography>} />
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        <Divider />

        {/* Stats */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="overline" sx={{ color: '#94A3B8', fontSize: 10, letterSpacing: 1.5 }}>Analysis</Typography>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Tooltip title="Total requirements extracted from your RFP" arrow placement="right">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'help' }}>
                <Typography variant="caption" color="#64748B">📋 Requirements</Typography>
                <Chip label={reqCount} size="small" sx={{ bgcolor: '#F3E8FF', color: '#6D28D9', fontWeight: 700, height: 22 }} />
              </Box>
            </Tooltip>
            <Tooltip title="Proposal sections to be generated" arrow placement="right">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'help' }}>
                <Typography variant="caption" color="#64748B">📑 Sections</Typography>
                <Chip label={sectionCount} size="small" sx={{ bgcolor: '#FCE7F3', color: '#DB2777', fontWeight: 700, height: 22 }} />
              </Box>
            </Tooltip>
            <Tooltip title="Diagrams to embed in the Word document" arrow placement="right">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'help' }}>
                <Typography variant="caption" color="#64748B">📊 Diagrams</Typography>
                <Chip label={diagramCount} size="small" sx={{ bgcolor: '#ECFDF5', color: '#059669', fontWeight: 700, height: 22 }} />
              </Box>
            </Tooltip>
            {coverage !== null && (
              <Tooltip title="How well the proposal covers RFP requirements" arrow placement="right">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'help' }}>
                  <Typography variant="caption" color="#64748B">✅ Compliance</Typography>
                  <Chip label={`${coverage}%`} size="small" sx={{ bgcolor: coverage >= 80 ? '#ECFDF5' : '#FEF3C7', color: coverage >= 80 ? '#059669' : '#D97706', fontWeight: 700, height: 22 }} />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Divider />

        {/* Chat Button - Sidebar */}
        <Box sx={{ px: 3, py: 2 }}>
          <Button fullWidth startIcon={<Chat />} onClick={() => setChatOpen(true)}
            sx={{ background: 'linear-gradient(135deg, #6D28D9, #4C1D95)', color: '#fff', py: 1.2,
              '&:hover': { background: 'linear-gradient(135deg, #5B21B6, #3B0764)', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(109,40,217,0.3)' } }}>
            Ask AI / Paste RFP
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center', fontSize: 10 }}>
            Cloud architecture Q&A • Paste text to generate
          </Typography>
        </Box>
      </Drawer>

      {/* Chat Panel */}
      <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)}
        onStartProposal={(result) => {
          setSession({ sessionId: result.session_id, fileName: result.file_name, fileCount: result.file_count });
          setStep(2);
          setChatOpen(false);
        }} />

      {/* Floating Chat Button - always visible */}
      <Tooltip title="Ask cloud architecture questions or paste RFP text" arrow placement="left">
        <Fab onClick={() => setChatOpen(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200,
            background: 'linear-gradient(135deg, #6D28D9, #DB2777)', color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg, #5B21B6, #BE185D)', transform: 'scale(1.05)' },
            boxShadow: '0 6px 20px rgba(109,40,217,0.4)' }}>
          <Chat />
        </Fab>
      </Tooltip>

      {/* Main */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: 'auto', maxHeight: '100vh' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>{STEPS[step - 1].label}</Typography>
            <Typography variant="body2" color="text.secondary">{STEPS[step - 1].desc}</Typography>
          </Box>
          <Chip label={`Step ${step} of 4`} sx={{ bgcolor: '#F3E8FF', color: '#6D28D9', fontWeight: 600 }} />
        </Box>

        {step === 1 && <StepUpload session={session} setSession={setSession} onNext={() => setStep(2)} />}
        {step === 2 && <StepSummary session={session} setSession={setSession} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
        {step === 3 && <StepConfig session={session} setSession={setSession} onNext={() => setStep(4)} onBack={() => setStep(2)} />}
        {step === 4 && <StepProposal session={session} setSession={setSession} onBack={() => setStep(3)} />}
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<CallbackPage />} />
          <Route path="/" element={<MainApp />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}
